import React, { useState, useEffect, useMemo } from 'react';
import { fetchPosts } from '../api/strapi';
import BlogSection from '../components/BlogSection';
import Footer from '../components/Footer';
import './BlogListPage.css';

export default function BlogListPage() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [sortBy, setSortBy] = useState('Latest');
  const [uniqueAuthors, setUniqueAuthors] = useState([]);

  useEffect(() => {
    fetchPosts()
      .then(fetchedPosts => {
        console.log('🔔 fetchedPosts:', fetchedPosts);
        if (!Array.isArray(fetchedPosts)) {
          setPosts([]);
          setUniqueAuthors([]);
          return;
        }
        setPosts(fetchedPosts);

        // Build a unique list of authors by name
        const authors = fetchedPosts
          .map(p => p.author)
          .filter(a => a && a.name)
          .reduce((acc, author) => {
            if (!acc.names.has(author.name)) {
              acc.names.add(author.name);
              acc.list.push(author);
            }
            return acc;
          }, { names: new Set(), list: [] }).list;

        console.log('🔔 uniqueAuthors:', authors);
        setUniqueAuthors(authors);
      })
      .catch(err => {
        console.error('Error fetching posts for BlogListPage:', err);
        setError(err.message);
        setPosts([]);
        setUniqueAuthors([]);
      });
  }, []);

  // Filter posts by search term and author
  const filteredPosts = useMemo(() => {
    if (!Array.isArray(posts)) return [];
    return posts.filter(p => {
      if (!p) return false;

      const textMatch = !searchTerm ||
        (p.title && p.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.summary && p.summary.toLowerCase().includes(searchTerm.toLowerCase()));

      const authorMatch =
        !authorFilter || (p.author && p.author.name === authorFilter);

      return textMatch && authorMatch;
    });
  }, [posts, searchTerm, authorFilter]);

  // Sort the filtered list
  const sortedPosts = useMemo(() => {
    const sorted = [...filteredPosts];
    const parseTime = str => {
      const t = Date.parse(str);
      return isNaN(t) ? 0 : t;
    };

    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'Oldest':
          return parseTime(a.date) - parseTime(b.date);
        case 'A-Z':
          return (a.title || '').localeCompare(b.title || '');
        case 'Z-A':
          return (b.title || '').localeCompare(a.title || '');
        case 'Latest':
        default:
          return parseTime(b.date) - parseTime(a.date);
      }
    });
    return sorted;
  }, [filteredPosts, sortBy]);

  if (error) return <div className="blogListError">Error: {error}</div>;
  if (posts === null) return <div className="blogListLoading">Loading…</div>;

  return (
    <>
      <main className="blogListPage">
        <div className="blogListHeader">
          <h1 className="blogListTitle">All posts</h1>

          <div className="blogListControls">
            <input
              type="text"
              placeholder="Search posts…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="blogSearchInput"
            />

            <select
              value={authorFilter}
              onChange={e => setAuthorFilter(e.target.value)}
              className="blogAuthorSelect"
            >
              <option value="">All authors</option>
              {uniqueAuthors.map(author => (
                <option key={author.name} value={author.name}>
                  {author.name}
                </option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="blogSortSelect"
            >
              <option value="Latest">Latest</option>
              <option value="Oldest">Oldest</option>
              <option value="A-Z">A-Z</option>
              <option value="Z-A">Z-A</option>
            </select>
          </div>
        </div>

        <BlogSection posts={sortedPosts} />
      </main>

      <div className="footerFullWidth">
        <Footer />
      </div>
    </>
  );
}
